import type {
  BillingAdjustmentType,
  BillingOverviewType,
  BooksZoneType,
  ConnectionStatusType,
  ConnectionType,
  ConsumerBillType,
  ConsumerDetailsType,
  ConsumerInfoType,
  ConsumersListType,
  ContactUsType,
  CreateAdjustmentBillType,
  dashboardOverviewType,
  GenerateBillType,
  PaginationType,
  PaymentAnalysisType,
  PaymentDetailsType,
  RecentPendingPaymentsType,
  SubmitConsumerInfoType,
  WeeklyPaymentsChartType,
  WeeklyPaymentsType,
  ZoneConnectionType,
  ZonePolicyDueDateType,
  ZoneType,
} from "@/types"
import { String } from "aws-sdk/clients/acm"
import { z } from "zod"

import {
  BillingInfoType,
  BillingUserInfoType,
  ConsumerConnectionBillingStatusType,
  DepartmentType,
  PositionType,
} from "@/types/billing"
import { MeterReaderZoneType } from "@/types/meter"
import { systemConfig } from "@/config/site"

import { getClientId } from "../auth"
import { meterSchema } from "../schema/meter"
import { apiFetch, formatDate } from "../utils"
import { postActivityLog } from "./log"

const billingUrl = `himo-billing/`

const editMeterSchema = meterSchema.extend({
  purchase_date: z.date().optional(),
  decommission_date: z.date().optional(),
})

export async function getRecentPayments() {
  const response = await apiFetch(`${billingUrl}recent-payment/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })
  if (!response.ok) {
    throw new Error(
      `Error fetching recent pending payments: ${response.statusText}`,
    )
  }
  return response.json()
}
export async function getRecentConsumerBill() {
  const response = await apiFetch(`${billingUrl}consumer-bill/recent/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })
  if (!response.ok) {
    throw new Error(
      `Error fetching recent consumer bill: ${response.statusText}`,
    )
  }
  const jsonResponse = await response.json()
  return jsonResponse
}

export async function getRecentBillAdjustment() {
  const response = await apiFetch(
    `${billingUrl}consumer-billing/adjustments/recent/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(
      `Error fetching recent consumer bill: ${response.statusText}`,
    )
  }
  return response.json()
}

export async function getRecentPendingPayments(): Promise<
  RecentPendingPaymentsType[]
> {
  const response = await apiFetch(`${billingUrl}recent-pending-payment/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })
  if (!response.ok) {
    throw new Error(`Error fetching recent payments: ${response.statusText}`)
  }
  return response.json()
}

export async function getBillingAdjustmentOverview(days: number) {
  const response = await apiFetch(
    `${billingUrl}bill-adjustment-overview/?days=${days}`,
  )

  if (!response.ok) {
    throw new Error("Cannot fetch billing adjustment overview")
  }

  return response.json()
}
export async function getBillingOverview(
  days: number,
): Promise<BillingOverviewType> {
  const response = await apiFetch(
    `${billingUrl}consumer-bill/overview/?days=${days}`,
  )

  if (!response.ok) {
    throw new Error("Cannot fetch billing overview")
  }

  return response.json()
}
export async function getDashboardOverview(
  days: number,
): Promise<dashboardOverviewType> {
  try {
    const response = await apiFetch(
      `${billingUrl}billing-dashboard-overview?days=${days}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
    if (!response.ok) {
      throw new Error(
        `Error fetching total active consumers: ${response.statusText}`,
      )
    }
    const jsonResponse = await response.json()
    return jsonResponse
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Error fetching billing overview: ${error.message}`)
    } else {
      throw new Error(
        "An unknown error occurred while fetching billing overview",
      )
    }
  }
}
export async function getWeeklyPayments(
  date: any,
): Promise<WeeklyPaymentsChartType[]> {
  const formattedDateFrom = formatDate(date.from)
  const formattedDateTo = formatDate(date.to)
  const response = await apiFetch(
    `${billingUrl}get-weekly-payment?start_date=${formattedDateFrom}&end_date=${formattedDateTo}/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(`Error fetching weekly payments: ${response.statusText}`)
  }

  const data: WeeklyPaymentsType = await response.json()

  return data.series_data.map((item) => ({
    time: item[0],
    paid: item[1],
    pending: item[2],
  }))
}

export async function getPaymentAnalysis(
  startDate: Date,
  endDate: Date,
): Promise<PaymentAnalysisType[]> {
  const response = await apiFetch(
    `${billingUrl}payment-analysis?from_date=${formatDate(startDate)}&to_date=${formatDate(endDate)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(`Error fetching payment analysis: ${response.statusText}`)
  }
  return response.json()
}

export async function getTotalMonthlyPayments(): Promise<{
  total_payments: string
}> {
  const response = await apiFetch(`${billingUrl}get-total-payments/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ date: "month" }),
  })
  if (!response.ok) {
    throw new Error(`Error fetching total payments: ${response.statusText}`)
  }
  return response.json()
}

export async function getConsumerBill(
  startDate: string,
  endDate: string,
  size: number,
  page: number,
  search: string,
  urlEndpoint: string,
  zone_id: number[],
  book_id: number[],
): Promise<PaginationType<ConsumerBillType>> {
  // Convert the array to a JSON string
  const zoneIdQuery =
    zone_id.length > 0 ? `zone_id=${JSON.stringify(zone_id)}` : ``
  const bookIdQuery =
    book_id.length > 0 ? `book_id=${JSON.stringify(book_id)}` : ``

  const response = await apiFetch(
    `${billingUrl}${urlEndpoint}from_date=${startDate}&to_date=${endDate}&size=${size}&page=${page}&${zoneIdQuery}&${bookIdQuery}&search=${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    if (response.status === 404) {
      return {
        count: 0,
        next: null,
        previous: null,
        results: [],
      } as PaginationType<ConsumerBillType>
    }
    throw new Error(`Error fetching: ${response.statusText}`)
  }

  const jsonResponse = await response.json()
  return jsonResponse
}

export async function getBillPerConsumer(
  consumerId: number,
  startDate: string,
  endDate: string,
  size: number,
  page: number,
  search: string,
): Promise<PaginationType<ConsumerBillType>> {
  const response = await apiFetch(
    `${billingUrl}consumer-bill/${consumerId}?from_date=${startDate}&to_date=${endDate}&size=${size}&page=${page}&search=${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error(`Error fetching consumer bill: ${response.statusText}`)
  }
  const jsonResponse = await response.json()
  return jsonResponse
}
export async function getRecentConsumerDetails(): Promise<
  ConsumerDetailsType[]
> {
  const response = await apiFetch(`${billingUrl}recently-added-consumers`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })
  if (!response.ok) {
    throw new Error(`Error fetching consumer details: ${response.statusText}`)
  }
  const jsonResponse = await response.json()
  //console.log("getRecentConsumerDetails: ", jsonResponse)
  return jsonResponse
}
export async function getConsumerDetails(
  size: number,
  page: number,
  search: string,
  zone_id: number[],
  book_id: number[],
): Promise<PaginationType<ConsumerDetailsType>> {
  //const clientId = getClientId()
  // Convert the array to a JSON string
  const zoneIdQuery =
    zone_id.length > 0 ? `zone_id=${JSON.stringify(zone_id)}` : ``
  const bookIdQuery =
    book_id.length > 0 ? `book_id=${JSON.stringify(book_id)}` : ``

  const response = await apiFetch(
    `${billingUrl}consumer/?&size=${size}&page=${page}&${zoneIdQuery}&${bookIdQuery}&search=${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(`Error fetching consumer details: ${response.statusText}`)
  }
  return response.json()
}

export async function getPaymentHistory(
  startDate: string,
  endDate: string,
): Promise<PaymentDetailsType[]> {
  console.log(startDate)
  console.log(endDate)

  const response = await apiFetch(`${billingUrl}get-payment-history/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      start_date: startDate,
      end_date: endDate,
    }),
  })
  if (!response.ok) {
    if (response.status === 404) return []
    throw new Error(`Error fetching payment history: ${response.statusText}`)
  }
  return response.json()
}

export async function getAllConsumers(): Promise<ConsumersListType[]> {
  const clientId = getClientId()

  const response = await apiFetch(
    `${billingUrl}get-all-consumers?client_id=${clientId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(`Error fetching all consumers: ${response.statusText}`)
  }
  return response.json()
}

export async function getZoneDropdown() {
  const dropdown = true
  const response = await apiFetch(`${billingUrl}zone?dropdown=${dropdown}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    throw new Error(`Error fetching zones: ${response.statusText}`)
  }

  const data = await response.json()
  return data.map((zone: ZoneType) => ({
    id: zone.zone_id,
    label: zone.zone_name,
    value: zone.zone_name,
  }))
}
export async function getZones(
  size: number,
  page: number,
  search: string,
): Promise<PaginationType<ZoneType>> {
  {
    const response = await apiFetch(
      `${billingUrl}zone?size=${size}&page=${page}&search=${search}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
    if (!response.ok) {
      throw new Error(`Error fetching zones: ${response.statusText}`)
    }
    const zones = await response.json()
    return zones
  }
}
export async function getBooksZone(zoneId: number) {
  const dropdown = true
  const response = await apiFetch(
    `${billingUrl}books-per-zone?zone_id=${zoneId}&dropdown=${dropdown}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(`Error fetching books zone: ${response.statusText}`)
  }

  const data = await response.json()
  //console.log(data)
  return data.map((book: BooksZoneType) => ({
    id: book.id,
    label: book.name,
    value: book.name,
    zone_id: book.zone_id,
    zone: book.zone,
  }))
}

export async function getMeterReaderZone(
  size: number,
  page: number,
  search: string,
  zone_id: number[],
): Promise<PaginationType<MeterReaderZoneType>> {
  // Convert the array to a JSON string
  const zoneIdQuery =
    zone_id.length > 0 ? `zone_id_filter=${JSON.stringify(zone_id)}` : ``

  const response = await apiFetch(
    `${billingUrl}meter-reader?size=${size}&page=${page}&${zoneIdQuery}&search=${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(`Error fetching meter reader: ${response.statusText}`)
  }

  const meterReader = await response.json()
  return meterReader
}
export async function getMeterReaderDropdown(zoneId: number) {
  const dropdown = true
  const response = await apiFetch(
    `${billingUrl}meter-reader?zone_id=${zoneId}&dropdown=${dropdown}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(`Error fetching meter reader zone: ${response.statusText}`)
  }

  const data = await response.json()
  return data.map((reader: MeterReaderZoneType) => ({
    id: reader.id,
    label: reader.name,
    value: reader.name,
  }))
}

export async function getConnectionType(isConnectionTypeDropdown: boolean) {
  const response = await apiFetch(`${billingUrl}connection-type/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })
  if (!response.ok) {
    throw new Error(`Error fetching connection type: ${response.statusText}`)
  }
  const data = await response.json()
  if (isConnectionTypeDropdown == true) {
    return data.map((item: ConnectionType) => ({
      id: item.id,
      label: item.type_name,
      value: item.type_name,
    }))
  }
  return data.map((item: ConnectionType) => ({
    ...item,
  }))
}

export async function getSizes() {
  const response = await apiFetch(`${billingUrl}connection-type/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })
  if (!response.ok) {
    throw new Error(`Error fetching connection type: ${response.statusText}`)
  }

  const data = await response.json()

  return data.map((item: ConnectionType) => ({
    id: item.id,
    label: item.type_name,
    value: item.type_name,
  }))
}

export async function getConnectionStatus() {
  const response = await apiFetch(`${billingUrl}connection-status/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })
  if (!response.ok) {
    throw new Error(`Error fetching connection status: ${response.statusText}`)
  }

  const data = await response.json()
  return data.map((item: ConnectionStatusType) => ({
    id: item.id,
    label: item.status_name,
    value: item.status_name,
  }))
}

//get connection status table data
export async function getConnectionStatusTableData() {
  const response = await apiFetch(`${billingUrl}connection-status/`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })
  if (!response.ok) {
    throw new Error(`Error fetching connection status: ${response.statusText}`)
  }

  const data = await response.json()
  return data.map((item: ConnectionStatusType) => ({
    ...item,
  }))
}

//Get zone policy table data
export async function getZonePolicyTableData(
  size: number,
  page: number,
  search: string,
  zone_id: number[],
): Promise<PaginationType<ZonePolicyDueDateType>> {
  // Convert the array to a JSON string
  const zoneIdQuery =
    zone_id.length > 0 ? `zone_id=${JSON.stringify(zone_id)}` : ``

  const response = await apiFetch(
    `${billingUrl}zone-policy-due-date/?page=${page}&size=${size}&${zoneIdQuery}&search=${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(`Error fetching zones: ${response.statusText}`)
  }
  const data = await response.json()
  return data
}

//Get specific zone policy data
export async function getZonePolicyMessage(
  id: number,
): Promise<ZonePolicyDueDateType> {
  const response = await apiFetch(
    `${billingUrl}templated-messages/email/${id}/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error(`Error fetching consumer: ${response.statusText}`)
  }
  return response.json()
}

//Add Zone Policy Due
export async function addZonePolicyDue(data: ZonePolicyDueDateType) {
  const response = await apiFetch(`${billingUrl}zone-policy-due-date/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-Zone Settings",
      sub_menu_path: "/zone-settings/zone-policy-due-date",
      action: `Add Zone Policy Due (ID ${data.zone_id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-Zone Settings",
    sub_menu_path: "/zone-settings/zone-policy-due-date",
    action: `Add Zone Policy Due (ID ${data.zone_id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return response.json()
}

//Edit Zone Policy Due
export async function editZonePolicyDue(
  id: number,
  data: ZonePolicyDueDateType,
) {
  const response = await apiFetch(`${billingUrl}zone-policy-due-date/${id}/`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-Zone Settings",
      sub_menu_path: "/zone-settings/zone-policy-due-date",
      action: `Edit Zone Policy Due (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-Zone Settings",
    sub_menu_path: "/zone-settings/zone-policy-due-date",
    action: `Edit Zone Policy Due (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return response.json()
}

//Delete Zone Policy Due
export async function deleteZonePolicyDue(id: number) {
  const response = await apiFetch(`${billingUrl}zone-policy-due-date/${id}/`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-Zone Settings",
      sub_menu_path: "/zone-settings/zone-policy-due-date",
      action: `Delete Zone Policy Due (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Failed to delete item with ID ${id}`)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-Zone Settings",
    sub_menu_path: "/zone-settings/zone-policy-due-date",
    action: `Delete Zone Policy Due (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return true
}

// get books per zone table data
export async function getBooksPerZoneTable(
  size: number,
  page: number,
  search: string,
  zone_id: number[],
): Promise<PaginationType<BooksZoneType>> {
  // Convert the array to a JSON string
  const zoneIdQuery =
    zone_id.length > 0 ? `zone_id_filter=${JSON.stringify(zone_id)}` : ``
  const response = await apiFetch(
    `${billingUrl}books-per-zone/?page=${page}&size=${size}&${zoneIdQuery}&search=${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(`Error fetching zones: ${response.statusText}`)
  }
  const booksDetails = await response.json()
  return booksDetails
}

// get books zone connection rate table data
export async function getZoneConnectionRateTable(
  size: number,
  page: number,
  search: string,
  zone_id: number[],
  connection_type_id: number[],
): Promise<PaginationType<ZoneConnectionType>> {
  // Convert the array to a JSON string
  const zoneIdQuery =
    zone_id.length > 0 ? `zone_id=${JSON.stringify(zone_id)}` : ``
  const connectionTypeIdQuery =
    connection_type_id.length > 0
      ? `connection_type_id=${JSON.stringify(connection_type_id)}`
      : ``
  const response = await apiFetch(
    `${billingUrl}zone-connection-rate/?page=${page}&size=${size}&${zoneIdQuery}&${connectionTypeIdQuery}&search=${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(`Error fetching zones: ${response.statusText}`)
  }
  const zoneRates = await response.json()
  return zoneRates
}

//get Position Table
export async function getPosition(
  size: number,
  page: number,
  search: string,
): Promise<PaginationType<PositionType>> {
  const response = await apiFetch(
    `position/?hirayatech_system=${systemConfig.hirayaSystemId}&page=${page}&size=${size}&search=${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(`Error fetching departments: ${response.statusText}`)
  }
  return await response.json()
}

//Add Position
export async function addPosition(data: PositionType) {
  const response = await apiFetch(`position/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...data,
      hirayatech_system_id: systemConfig.hirayaSystemId,
    }),
  })

  if (!response.ok) {
    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-User & Role Management",
      sub_menu_path: "/user-role-management/position",
      action: `Add Position (${data.name})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-User & Role Management",
    sub_menu_path: "/user-role-management/position",
    action: `Add Position (${data.name})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return response.json()
}

//Edit Position
export async function editPosition(id: number, data: DepartmentType) {
  const response = await apiFetch(`position/${id}/`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...data,
      hirayatech_system_id: systemConfig.hirayaSystemId,
    }),
  })

  if (!response.ok) {
    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-User & Role Management",
      sub_menu_path: "/user-role-management/position",
      action: `Edit Position (${data.name})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-User & Role Management",
    sub_menu_path: "/user-role-management/position",
    action: `Edit Position (${data.name})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return response.json()
}

//Delete Position
export async function deletePosition(id: number) {
  const response = await apiFetch(`position/${id}/`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-User & Role Management",
      sub_menu_path: "/user-role-management/position",
      action: `Delete Position (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-User & Role Management",
    sub_menu_path: "/user-role-management/position",
    action: `Delete Position (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return true
}

//get Department Table
export async function getDepartment(
  size: number,
  page: number,
  search: string,
): Promise<PaginationType<DepartmentType>> {
  const response = await apiFetch(
    `department/?hirayatech_system=${systemConfig.hirayaSystemId}&page=${page}&size=${size}&search=${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error(`Error fetching departments: ${response.statusText}`)
  }

  return await response.json()
}

export async function getPositionPerDepartmentDropdown(
  department_id: number | undefined,
) {
  const response = await apiFetch(
    `department/${department_id}/position/?hirayatech_system=${systemConfig.hirayaSystemId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error(
      `Error fetching position per department: ${response.statusText}`,
    )
  }

  const data = await response.json()
  return data.map((position: PositionType) => ({
    id: position.id,
    label: position.name,
    value: position.name,
  }))
}

//Add Post/ Department
export async function addDepartment(data: DepartmentType) {
  const response = await apiFetch(`department/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...data,
      hirayatech_system_id: systemConfig.hirayaSystemId,
    }),
  })

  if (!response.ok) {
    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-User & Role Management",
      sub_menu_path: "/user-role-management/department",
      action: `Add Department (${data.name})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-User & Role Management",
    sub_menu_path: "/user-role-management/department",
    action: `Add Department (${data.name})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return response.json()
}

//Edit Department
export async function editDepartment(id: number, data: DepartmentType) {
  const response = await apiFetch(`department/${id}/`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...data,
      hirayatech_system_id: systemConfig.hirayaSystemId,
    }),
  })

  if (!response.ok) {
    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-User & Role Management",
      sub_menu_path: "/user-role-management/department",
      action: `Edit Department (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-User & Role Management",
    sub_menu_path: "/user-role-management/department",
    action: `Edit Department (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return response.json()
}

//Delete Department
export async function deleteDepartment(id: number) {
  const response = await apiFetch(`department/${id}/`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    const responseText = await response.text()

    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-User & Role Management",
      sub_menu_path: "/user-role-management/department",
      action: `Delete Department (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(
      `Failed to delete department (ID: ${id}). Response: ${responseText}`,
    )
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-User & Role Management",
    sub_menu_path: "/user-role-management/department",
    action: `Delete Department (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return true
}

//Submit Contact Form
export async function submitContactForm(data: ContactUsType) {
  const response = await apiFetch(`contact-us/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const responseText = await response.text()
    throw new Error(`${responseText}`)
  }
  return true
}

export async function getConsumer(
  consumerId: number,
): Promise<ConsumerInfoType> {
  const response = await apiFetch(`${billingUrl}consumer/${consumerId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    throw new Error(`Error fetching consumer: ${response.statusText}`)
  }
  const jsonResponse = await response.json()
  return jsonResponse
}

export async function getConnectionAndBillingStatus(): Promise<
  ConsumerConnectionBillingStatusType[]
> {
  const response = await apiFetch(
    `${billingUrl}get-connection-and-billing-status/`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    throw new Error(`Error fetching: ${response.statusText}`)
  }

  return (await response.json()).consumers
}

export async function getBillingAdjustmentList(
  size: number,
  page: number,
  search: string,
): Promise<PaginationType<BillingAdjustmentType>> {
  const response = await apiFetch(
    `${billingUrl}consumer-billing/adjustments/?size=${size}&page=${page}&search=${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    if (response.status === 404) {
      return {
        count: 0,
        next: null,
        previous: null,
        results: [],
      } as PaginationType<BillingAdjustmentType>
    }
    throw new Error(`Error fetching: ${response.statusText}`)
  }
  //console.log(response.json)

  return response.json()
}
export async function approveDenyBillingAdjustment(
  id: number,
  billingAdjustmentStatus: string,
) {
  const data = {
    billing_adjustment_status: billingAdjustmentStatus,
  }

  const response = await apiFetch(
    `${billingUrl}consumer-billing/adjustments/update/${id}/`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  )
  if (!response.ok) {
    if (response.status === 404) return []
    throw new Error(`Error fetching: ${response.statusText}`)
  }
  return response.json()
}
// Operations
export async function generatePdfBill(data: GenerateBillType) {
  const response = await apiFetch(`${billingUrl}generate-pdf-bill/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    await postActivityLog({
      menu_path: "/billing",
      sub_menu_path: "",
      action: `Generate Bill (${data.account_num})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "/billing",
    sub_menu_path: "",
    action: `Generate Bill (${data.account_num})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return response.json()
}

export async function addConsumer(data: SubmitConsumerInfoType) {
  const response = await apiFetch(`${billingUrl}consumer/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    await postActivityLog({
      menu_path: "/manage-consumers",
      sub_menu_path: "",
      action: `Add Consumer (${data.full_name})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "/manage-consumers",
    sub_menu_path: "",
    action: `Add Consumer (${data.full_name})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return response.json()
}

export async function editConsumer(id: number, data: SubmitConsumerInfoType) {
  const response = await apiFetch(`${billingUrl}consumer/${id}/`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    await postActivityLog({
      menu_path: "/manage-consumers",
      sub_menu_path: "",
      action: `Edit Consumer (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  const result = await response.json()

  await postActivityLog({
    menu_path: "/manage-consumers",
    sub_menu_path: "",
    action: `Edit Consumer (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return result
}

export async function deleteConsumer(id: number) {
  const response = await apiFetch(`${billingUrl}consumer/${id}/`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    await postActivityLog({
      menu_path: "/manage-consumers",
      sub_menu_path: "",
      action: `Delete Consumer (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "/manage-consumers",
    sub_menu_path: "",
    action: `Delete Consumer (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  if (response.status === 204) {
    return null
  }

  const contentType = response.headers.get("content-type")
  if (contentType && contentType.includes("application/json")) {
    return response.json()
  } else {
    return null
  }
}

export async function payBill(data: BillingInfoType) {
  const response = await apiFetch(`${billingUrl}pay-bill/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    await postActivityLog({
      menu_path: "/billing",
      sub_menu_path: "",
      action: `Pay Bill (₱${data.amount_paid})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "/billing",
    sub_menu_path: "",
    action: `Pay Bill (₱${data.amount_paid})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return response.json()
}

export async function addMeter(data: z.infer<typeof meterSchema>) {
  const response = await apiFetch(`${billingUrl}meter-info/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    await postActivityLog({
      menu_path: "/meter-info",
      sub_menu_path: "",
      action: `Add Meter (${data.serial_number})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "/meter-info",
    sub_menu_path: "",
    action: `Add Meter (${data.serial_number})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return response.json()
}

export async function editMeter(
  id: number,
  data: z.infer<typeof editMeterSchema>,
) {
  const response = await apiFetch(`${billingUrl}meter-info/${id}/`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    await postActivityLog({
      menu_path: "/meter-info",
      sub_menu_path: "",
      action: `Edit Meter (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  const result = await response.json()

  await postActivityLog({
    menu_path: "/meter-info",
    sub_menu_path: "",
    action: `Edit Meter (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return result
}

export async function deleteMeter(id: number) {
  const response = await apiFetch(`${billingUrl}meter-info/${id}/`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    await postActivityLog({
      menu_path: "/meter-info",
      sub_menu_path: "",
      action: `Delete Meter (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Error: ${response.statusText}`)
  }

  await postActivityLog({
    menu_path: "/meter-info",
    sub_menu_path: "",
    action: `Delete Meter (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  if (response.status === 204) {
    return null
  }

  const contentType = response.headers.get("content-type")
  if (contentType && contentType.includes("application/json")) {
    return response.json()
  } else {
    return null
  }
}

export async function sendEmailReminder(
  billingIds: number[],
  reminderType: string,
) {
  const response = await apiFetch(
    `${billingUrl}email-blast/?reminder_type=${reminderType}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        billing_id_list: billingIds,
      }),
    },
  )
  if (!response.ok) {
    if (response.status === 404) return []
    throw new Error(`Error sending ${reminderType}: ${response.statusText}`)
  }
  return response.json()
}

export async function createBillAdjustment(
  billingId: number,
  data: CreateAdjustmentBillType,
) {
  data["billing_id"] = billingId
  const response = await apiFetch(
    `${billingUrl}consumer-billing/adjustments/create/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  )
  const result = await response.json()
  return result
}

export async function getDepartmentDropdown() {
  const dropdown = true
  const response = await apiFetch(
    `department/?hirayatech_system=${systemConfig.hirayaSystemId}&dropdown=${dropdown}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error(`Error fetching zones: ${response.statusText}`)
  }

  const data = await response.json()
  return data.map((department: DepartmentType) => ({
    id: department.id,
    label: department.name,
    value: department.name,
  }))
}

export async function getPositionDropdown() {
  const dropdown = true
  const response = await apiFetch(
    `position/?hirayatech_system=${systemConfig.hirayaSystemId}&dropdown=${dropdown}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error(`Error fetching zones: ${response.statusText}`)
  }

  const data = await response.json()
  return data.map((position: PositionType) => ({
    id: position.id,
    label: position.name,
    value: position.name,
  }))
}

export async function getBillingUserInfo(
  size: number,
  page: number,
  search: string,
): Promise<PaginationType<BillingUserInfoType>> {
  const response = await apiFetch(
    `${billingUrl}users?size=${size}&page=${page}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )
  if (!response.ok) {
    if (response.status === 404) {
      return {
        count: 0,
        next: null,
        previous: null,
        results: [],
      } as PaginationType<BillingUserInfoType>
    }
    throw new Error(`Error fetching: ${response.statusText}`)
  }

  const jsonResponse = await response.json()
  return jsonResponse
}

export async function getBillingUsersDropdown() {
  const response = await apiFetch(`${billingUrl}users/dropdown`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    throw new Error(`Error fetching users: ${response.statusText}`)
  }

  const data = await response.json()
  return data.map((billing: BillingUserInfoType) => ({
    id: billing.id,
    label: billing.username,
    value: billing.username,
  }))
}

export async function getUsersDropdown() {
  const response = await apiFetch(`${billingUrl}users/assign`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    throw new Error(`Error fetching users: ${response.statusText}`)
  }

  const data = await response.json()

  return data.map((user: BillingUserInfoType) => ({
    id: user.id,
    label: user.username,
    value: user,
  }))
}

export async function addBillingUser(data: BillingUserInfoType) {
  const response = await apiFetch(`${billingUrl}users/add/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    let errorDetails = ""
    try {
      const errorBody = await response.json()
      errorDetails = errorBody.error || "Unknown error occurred"
    } catch (e) {
      errorDetails = "Unable to parse error body"
    }

    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-User & Role Management",
      sub_menu_path: "/user-role-management/billing-user-info",
      action: `Add Billing User (${data.username})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`${errorDetails}`)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-User & Role Management",
    sub_menu_path: "/user-role-management/billing-user-info",
    action: `Add Billing User (${data.username})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return response.json()
}

export async function editBillingUserInfo(
  id: number,
  data: BillingUserInfoType,
) {
  //console.log(data)

  const response = await apiFetch(`${billingUrl}users/update/${id}/`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    let errorMessage = `Error: ${response.statusText}`

    try {
      const errorData = await response.json() // Read the response body
      console.error("API Error Response:", errorData)

      if (errorData.non_field_errors) {
        errorMessage = errorData.non_field_errors.join(", ")
      } else if (errorData.message) {
        errorMessage = errorData.message
      } else if (typeof errorData === "object" && errorData !== null) {
        errorMessage = Object.entries(errorData)
          .map(([key, value]) =>
            Array.isArray(value) ? `${value.join(", ")}` : `${value}`,
          )
          .join("\n") // Newline for better readability
      } else {
        errorMessage = JSON.stringify(errorData) // Fallback
      }
    } catch (parseError) {
      console.error("Error parsing response:", parseError)
    }

    //console.log(errorMessage)

    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-User & Role Management",
      sub_menu_path: "/user-role-management/billing-user-info",
      action: `Edit Billing User Info (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(errorMessage)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-User & Role Management",
    sub_menu_path: "/user-role-management/billing-user-info",
    action: `Edit Billing User Info (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return response.json()
}

export async function deleteBillingUserInfo(id: number) {
  const response = await apiFetch(`${billingUrl}users/delete/${id}/`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  })

  //console.log(response)

  if (!response.ok) {
    await postActivityLog({
      menu_path: "HIMO Billing-Digos Water District-User & Role Management",
      sub_menu_path: "/user-role-management/billing-user-info",
      action: `Delete Billing User (ID ${id})`,
      status_code: response.status,
      api_endpoint: response.url,
      web_url: window.location.href,
    })

    throw new Error(`Failed to delete user with ID ${id}`)
  }

  await postActivityLog({
    menu_path: "HIMO Billing-Digos Water District-User & Role Management",
    sub_menu_path: "/user-role-management/billing-user-info",
    action: `Delete Billing User (ID ${id})`,
    status_code: response.status,
    api_endpoint: response.url,
    web_url: window.location.href,
  })

  return true
}

export async function getBillingAdjustmentDailyCount(
  fromDate: string | Date,
  toDate: string | Date,
) {
  const formattedFromDate = formatDate(fromDate)
  const formattedToDate = formatDate(toDate)

  const response = await apiFetch(
    `${billingUrl}bill-adjustment-daily-count/?from_date=${formattedFromDate}&to_date=${formattedToDate}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error(
      `Error fetching bill adjustment daily count: ${response.statusText}`,
    )
  }

  const jsonResponse = await response.json()

  return jsonResponse.bill_adjustment_count
}

export async function getBillingAdjustmentPieData(
  fromDate: string | Date,
  toDate: string | Date,
) {
  const formattedFromDate = formatDate(fromDate)
  const formattedToDate = formatDate(toDate)
  const response = await apiFetch(
    `${billingUrl}bill-adjustment-piechart/?from_date=${formattedFromDate}&to_date=${formattedToDate}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  )

  if (!response.ok) {
    throw new Error(
      `Error fetching bill adjustment daily count: ${response.statusText}`,
    )
  }

  const jsonResponse = await response.json()
  return jsonResponse.billing_adjustment_distribution
}
